import type { MultiFileItem } from './VideoOrderModal';
import { VideoThumb } from './VideoThumb';

const LINK_BASE = 'text-xs hover:underline disabled:opacity-50 disabled:cursor-not-allowed';
const LINK = `${LINK_BASE} text-biscayne`;
const LINK_DESTRUCTIVE = `${LINK_BASE} text-red-500`;

function itemName(item: MultiFileItem): string {
  return item.type === 'local' ? item.file.name : item.file.displayName.replace(/\s+—\s+/g, ' · ');
}

/** The videos a player has picked to stitch, in upload order. */
export function SelectedVideosList({
  items,
  disabled,
  onAdd,
  onReorder,
  onRemoveAll,
  onRemoveItem,
}: {
  items: MultiFileItem[];
  disabled?: boolean;
  onAdd: () => void;
  onReorder: () => void;
  onRemoveAll: () => void;
  onRemoveItem: (index: number) => void;
}) {
  return (
    <div className="mb-3" data-testid="selected-videos-list">
      <div className="mb-2 flex items-center justify-end gap-4">
        <button type="button" data-testid="selected-videos-add" onClick={onAdd} disabled={disabled} className={LINK}>
          Add
        </button>
        <button type="button" data-testid="selected-videos-reorder" onClick={onReorder} disabled={disabled} className={LINK}>
          Reorder
        </button>
        <button type="button" data-testid="selected-videos-remove-all" onClick={onRemoveAll} disabled={disabled} className={LINK_DESTRUCTIVE}>
          Remove All
        </button>
      </div>
      <div className="bg-white rounded-[12px] overflow-hidden">
        {items.map((item, i) => {
          const name = itemName(item);
          return (
            <div key={`${i}-${name}`} className="pl-4" data-testid="selected-video-row">
              <div className={`flex min-h-16 items-center gap-3 py-2 pr-2 ${i > 0 ? 'border-t border-[#e6e6e6]' : ''}`}>
                <VideoThumb item={item} />
                <div className="min-w-0 flex-1">
                  <div className="[overflow-wrap:anywhere] text-[15px] leading-[20px] tracking-[-0.23px] text-black">
                    {name}
                  </div>
                  <div className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">
                    {item.type === 'local' ? 'From device' : 'From Double Bagel Cloud'}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="selected-video-remove"
                  onClick={() => onRemoveItem(i)}
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                  className="relative after:absolute after:-inset-1 after:content-[''] ios-press flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(120,120,128,0.16)] disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
                >
                  <svg width="14" height="14" fill="none" stroke="#1C1C1C" strokeWidth="2.25" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">
        These are joined into one video, in this order, before upload.
      </p>
    </div>
  );
}
